import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShapeSquare, ShapeCircle, ShapeFlower, ShapeRainbow } from "./brand/Shapes";
import CambridgeLogo from "./brand/CambridgeLogo";
import { ArrowRight } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="home"
      className="relative pt-32 md:pt-60 pb-12 md:pb-28 overflow-hidden bg-[#FAFAF5]"
      data-testid="hero-section"
    >
      {/* Decorative floating shapes */}
      <ShapeSquare
        size={120}
        className="absolute top-24 right-[8%] floaty opacity-90 hidden md:block"
        color="#648DE5"
      />
      <ShapeCircle
        size={72}
        className="absolute bottom-16 left-[6%] floaty opacity-80 hidden md:block"
        color="#3AB0D6"
      />
      <ShapeFlower
        size={88}
        className="absolute top-[55%] left-[4%] floaty hidden lg:block"
        color="#C6D947"
      />

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
              data-testid="hero-badge"
            >
              <CambridgeLogo height={56} testid="hero-cambridge-logo" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-[#0F1B47] text-[40px] sm:text-6xl lg:text-7xl xl:text-[72px] leading-[1.05] tracking-tight font-semibold"
              data-testid="hero-heading"
            >
              Where small
              <br />
              wonders become
              <br />
              <span className="italic font-normal relative">
                lifelong thinkers
                <svg
                  className="absolute -bottom-3 left-0 w-full"
                  height="14"
                  viewBox="0 0 420 14"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 10 C 100 2, 220 14, 418 6"
                    stroke="#C6D947"
                    strokeWidth="6"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
              .
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-6 md:mt-8 max-w-xl text-[#4A5568] text-[15px] md:text-lg leading-relaxed font-light"
              data-testid="hero-description"
            >
              Naavi Early Years is an international Cambridge-affiliated preschool for children
              2 — 6. A calm, rigorous, beautifully crafted start to school — built on play,
              presence and the quiet science of how children truly learn.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Link
                to="/admissions#enquiry"
                className="group inline-flex items-center justify-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] hover:shadow-xl"
                data-testid="hero-apply-btn"
              >
                Enquire
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
              <a
                href="#schedule-visit"
                className="inline-flex items-center justify-center bg-transparent border-2 border-[#0F1B47] text-[#0F1B47] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:bg-[#0F1B47] hover:text-[#FDFDF9]"
                data-testid="hero-schedule-btn"
              >
                Schedule a Campus Visit
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-4"
            />
          </div>

          {/* RIGHT: image collage */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-[36px] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(15,27,71,0.35)]">
                <img decoding="async" loading="eager" fetchpriority="high"
                  src="https://customer-assets.emergentagent.com/job_naavi-preschool/artifacts/65lknuu8_IMG-20260313-WA0045.jpg"
                  alt="Children at Naavi Early Years dressed up for theme day"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B47]/20 via-transparent to-transparent" />
              </div>

              {/* Floating rainbow shape */}
              <div className="absolute -top-6 -left-8 bg-[#FDFDF9] rounded-3xl p-5 shadow-[0_20px_50px_-20px_rgba(15,27,71,0.3)] floaty hidden md:block">
                <ShapeRainbow size={88} color="#5BC4B8" />
              </div>

              {/* Floating stat card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="absolute -bottom-8 -right-4 md:-right-10 bg-[#FDFDF9] rounded-3xl p-5 pr-7 shadow-[0_24px_60px_-20px_rgba(15,27,71,0.3)] border border-[#0F1B47]/5 max-w-[260px]"
                data-testid="hero-floating-card"
              >
                <div className="flex items-center gap-3">
                  <ShapeCircle size={36} color="#C6D947" />
                  <div>
                    <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">
                      Admissions Open
                    </div>
                    <div className="text-[#0F1B47] font-semibold text-sm mt-0.5">
                      2026 — 27
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat() {
  return null;
}
